<template>
  <i-container>
    <div :class="{ 'flex-container': isBigScreen, 'flex-column': !isBigScreen }">
      <i-form :schema="form" @keyup.enter="filter" :class="{ 'element1b': isBigScreen, 'element1s': !isBigScreen }">
        <i-form-group>
          <i-input :schema="form.cospar" v-model="form.cospar.value" placeholder="COSPAR or name" @keyup.enter="filter"></i-input>
        </i-form-group>
        <i-form-group>
          <i-input :schema="form.norad" v-model="form.norad.value" placeholder="NORAD"></i-input>
        </i-form-group>
        <i-form-group id="minp">
          <i-input :schema="form.minp" v-model="form.minp.value" placeholder="Minimal period"></i-input>
        </i-form-group>
        <i-form-group id="maxp">
          <i-input :schema="form.maxp" v-model="form.maxp.value" placeholder="Maximal period"></i-input>
        </i-form-group>
        <i-form-group id="date-input">
          <v-date-picker
            mode="range"
            v-model="form.date.value"
            :popover="{ placement: 'bottom', visibility: 'click' }"
            :columns="$screens({ default: 1, md: 2 })">
            <i-input
              slot-scope="{ inputProps, inputEvents, isDragging }"
              placeholder="Select date"
              v-bind="inputProps"
              v-on="inputEvents" readonly>
              <i slot="suffix">
                <i-button
                  type="button"
                  circle size="sm"
                  @click="test($event)">×
                  <!--                                <img src="./../assets/times-circle-regular.svg" alt="Delete"/>-->
                </i-button>
              </i>
            </i-input>
          </v-date-picker>
        </i-form-group>
        <i-form-group id="button-form-group" class="_clearfix">
          <i-button type="submit" class="_margin-left-1-2 _float-right" outline variant="primary" @click="filter">Filter</i-button>
          <i-button class="_float-right" outline variant="danger" @click="clearFilter">Clear filter</i-button>
          <i-button class="_float-left" outline variant="info" @click="visible = true">Help</i-button>

          <p class="_float-left _width-65 _text-left _font-size-sm _margin-0 _padding-0 _padding-left-1
                    _display-xs-none _display-sm-none _display-md-none _display-lg-block">
            <strong>Citation</strong>: If the available data are used in any scientific or academic
            publications, this work should be properly cited and
            acknowledged.  Please, use the
            <a href="https://www.sciencedirect.com/science/article/pii/S0273117720300727">following reference to cite the work</a>.
            <br>
            <strong>Ephemeris</strong>: To get the object’s ephemeris, visit online service
            <a href="https://satfield.astros.eu/"> SatField</a>.
          </p>

          <p class="_float-left _width-100 _text-left _font-size-sm _margin-0 _padding-0 _padding-left-1
                    _display-xs-block _display-sm-block _display-md-block _display-lg-none _display-xl-none">
            <strong>Citation</strong>: If the available data are used in any scientific or academic
            publications, this work should be properly cited and
            acknowledged.  Please, use the
            <a href="https://www.sciencedirect.com/science/article/pii/S0273117720300727">following reference to cite the work</a>.
            <br>
            <strong>Ephemeris</strong>: To get the object’s ephemeris, visit online service
            <a href="https://satfield.astros.eu/"> SatField</a>.
          </p>
        </i-form-group>
      </i-form>
      <i-table striped responsive :class="{ 'element2b': isBigScreen, 'element2s': !isBigScreen }">
        <tbody>
        <tr v-for="row in table">
          <td class="custom-table-left">{{ row.colname }}</td>
          <td class="custom-table-right">{{ row.colvalue }}</td>
        </tr>
        </tbody>
      </i-table>
    </div>

    <i-modal id="help-modal" variant="info" v-model="visible" size="lg">
            <template slot="header">Help</template>
            <div class="row">
                <h4 class="column _margin-top-0">Light curve properties</h4>
            </div>
            <dl class="row">
                <dt class="column -xs-3">Source</dt>
                <dd class="column -xs-9">ASCII file containing the light curve data.</dd>

                <dt class="column -xs-3">Period [sec]</dt>
                <dd class="column -xs-9">Value of the synodic period. <br>
                  Following integer numbers are reserved for internal type identification
                  and not represent apparent rotation period value: 5000, 6000, 7000, 9999.
                </dd>

                <dt class="column -xs-3">Period error [sec]</dt>
                <dd class="column -xs-9">Uncertainty of the synodic period estimation.</dd>

                <dt class="column -xs-3">ADU error</dt>
                <dd class="column -xs-9">Average uncertainty of the intensity in the ADU units.</dd>

                <dt class="column -xs-3">Magnitude error</dt>
                <dd class="column -xs-9">Average uncertainty of the intensity in the magnitudes.</dd>

                <dt class="column -xs-3">Number of points</dt>
                <dd class="column -xs-9">Number of measurement points in the light curve.</dd>

                <dt class="column -xs-3">Exposure [sec]</dt>
                <dd class="column -xs-9">Exposure time used during measurement acquisition.</dd>

                <dt class="column -xs-3">APD [sec]</dt>
                <dd class="column -xs-9">Average point distance.</dd>

                <dt class="column -xs-3">MedPD [sec]</dt>
                <dd class="column -xs-9">Median distance/duration between two measurement points in the light curve.</dd>

                <dt class="column -xs-3">ModePD [sec]</dt>
                <dd class="column -xs-9">Moderate distance/duration between two measurement points in the light curve.</dd>

                <dt class="column -xs-3">STDPD [sec]</dt>
                <dd class="column -xs-9">Standard deviation of the point distance.</dd>

                <dt class="column -xs-3">Filter</dt>
                <dd class="column -xs-9">Used type of filter. Available are C and <a href="http://www.flicamera.com/filters/index.html">Johnson-Cousins BVRI filters.</a></dd>

                <dt class="column -xs-3">AmpMath [mag]</dt>
                <dd class="column -xs-9">Mathematical amplitude of the light curve calculated as difference of the global extremes of the optimized Fourier function.</dd>

                <dt class="column -xs-3">AmpDat [mag]</dt>
                <dd class="column -xs-9">Measured amplitude of the light curve calculated as difference of the global extremes of the light curve. Global extremes were determined as a mean value of data points in an interval ±0.0025mag around the global extremes.</dd>

                <dt class="column -xs-3">AMErr [mag]</dt>
                <dd class="column -xs-9">Value of the root-mean-square calculated as:<br><img class="lazyload image -responsive" width="45%" src="/static/formulas/AMErr.png" alt="AMErr formula"/>
                    <br>where O<sub>i</sub> are measured data points, C<sub>i</sub> corresponding calculated values of the Fourier function and N is the total number of the measurements in the datasets. The parameter AMErr [mag] serves as input value for σ(m) in the next uncertainty calculations.</dd>

                <dt class="column -xs-3">AmpErr [mag]</dt>
                <dd class="column -xs-9">Uncertainty of the mathematical amplitude estimation using:<br>
                                        <img class="lazyload image -responsive" width="30%" src="/static/formulas/AmpErr.png" alt="AmpErr formula"/><br>
                                        where N is total number of measurements in the data set and σ(m) is a uncertainty in the magnitudes (Montgomery and O’Donoghue, 1999).</dd>

                <dt class="column -xs-3">PhErr</dt>
                <dd class="column -xs-9">Uncertainty of the phase estimation calculated using:<br>
                                        <img class="lazyload image -responsive" width="30%" src="/static/formulas/PhErr.png" alt="PhErr formula"/><br>
                                        where N is total number of measurements in the data set, a is the amplitude of the light curve and σ(m) is a uncertainty in the magnitudes (Montgomery and O’Donoghue, 1999).</dd>

                <dt class="column -xs-3">fErr [Hz]</dt>
                <dd class="column -xs-9">Uncertainty of the frequency estimation calculated using:<br>
                                        <img class="lazyload image -responsive" width="45%" src="/static/formulas/fErr.png" alt="fErr formula"/><br>
                                        where N is total number of measurements in the data set, a is the amplitude of the light curve, ∆t is time distance of points in the dataset (e.g. APD parameter) and σ(m) is a uncertainty in the magnitudes (Montgomery and O’Donoghue, 1999).</dd>

                <dt class="column -xs-3">PErr [sec]</dt>
                <dd class="column -xs-9">Uncertainty of the period estimation calculated using:<br>
                                        <img class="lazyload image -responsive" width="45%" src="/static/formulas/PErr.png" alt="PErr formula"/><br>
                                        where N is total number of measurements in the data set, a is the amplitude of the light curve, ∆t is time distance of points in the dataset (e.g. APD parameter), P is determined value of the period and σ(m) is a uncertainty in the magnitudes (Montgomery and O’Donoghue, 1999).</dd>

            </dl>

            <div class="row">
                <h4 class="column _margin-top-1">Object and orbital properties</h4>
            </div>
            <dl class="row">
                <dt class="column -xs-3">Object name</dt>
                <dd class="column -xs-9">Name of the object as published in USSTRATCOM satellite situation report (SSR).</dd>

                <dt class="column -xs-3">Object type</dt>
                <dd class="column -xs-9">Type of the object as published in USSTRATCOM SSR.</dd>

                <dt class="column -xs-3">NORAD</dt>
                <dd class="column -xs-9">NORAD number of the object.</dd>

                <dt class="column -xs-3">Orbital period [min]</dt>
                <dd class="column -xs-9">Orbital period as published in USSTRATCOM SSR.</dd>

                <dt class="column -xs-3">Inclination [deg]</dt>
                <dd class="column -xs-9">Orbital inclination as published in USSTRATCOM SSR.</dd>

                <dt class="column -xs-3">Perigee [km]</dt>
                <dd class="column -xs-9">Mean perigee altitude above the Earth surface as published in USSTRATCOM SSR.</dd>

                <dt class="column -xs-3">Apogee [km]</dt>
                <dd class="column -xs-9">Mean apogee altitude above the Earth surface as published in USSTRATCOM SSR.</dd>

                <dt class="column -xs-3">Decayed</dt>
                <dd class="column -xs-9">Information whether the object is already decayed as published in USSTRATCOM SSR.</dd>
            </dl>

            <div class="row">
                <h4 class="column _margin-top-1">References</h4>
            </div>

            <div class="row">
                <div class="column -xs-12 _margin-bottom-1 _font-size-sm">
                    M. Hamara. Rotation properties of the articial objects in the vicinity of Earth, Dissertation thesis. PhD thesis, Comenius University in Bratislava; Faculty of Mathematics, Physics and Informatics Bratislava, 2017.
                </div>
                <div class="column -xs-12 _font-size-sm">
                    M.H. Montgomery and D O’Donoghue. A derivation of the errors for least squares fitting to time series data. Delta Scuti Star Newsletter, 13:28, 1999.
                </div>
            </div>
            <template slot="footer">
                <div>
                    If you have further question, don't hesitate to contact us at <a href="mailto:jiri.silha@fmph.uniba.sk">jiri.silha@fmph.uniba.sk</a>.
                </div>
                <div>
                  link to database in json format: <a href='/static/data/database.json'>database</a>
                </div>
            </template>
        </i-modal>
  </i-container>
</template>

<script>
    export default {
        name: "LCDForm",
        data() {
            return {
                visible: false,
                isBigScreen: window.innerWidth > 600,
                table: [
                  {colname: "light curves", colvalue: "2224"},
                  {colname: "oldest", colvalue: "2016-01-18"},  // 2017-08-13 03:12:34
                  {colname: "latest", colvalue: "2024-06-25"},  // 2024-06-25 01:05:02
                  {colname: "objects", colvalue: "791"},
                  {colname: "spacecrafts", colvalue: "212"},
                  {colname: "rocket bodies", colvalue: "521"},
                  {colname: "debris", colvalue: "58"},
                  {colname: "objects with period", colvalue: "419"},
                ],
                form: this.$form({
                    cospar: {
                        value: ''
                    },
                    norad: {
                        value: '',
                        validateOn: 'input',
                        validators: [
                            {
                                rule: 'custom',
                                validator: function (v) {
                                    if (v.length === 0) return true;
                                    return /^\d+$/i.test(v);
                                },
                                message: 'Please enter only numbers or keep this field empty.'
                            }
                        ]
                    },
                    minp: {
                        value: '',
                        validateOn: 'input',
                        validators: [
                            {
                                rule: 'custom',
                                validator: function (v) {
                                    if (v.length === 0) return true;
                                    let valid = !/^\s*$/.test(v) && !isNaN(v);
                                    if (!valid) return false;
                                    let value = parseFloat(v);
                                    return value >= 0;
                                },
                                message: 'Please enter only positive numbers (can be decimal) or keep this field empty.'
                            }
                        ]
                    },
                    maxp: {
                        value: '',
                        validateOn: 'input',
                        validators: [
                            {
                                rule: 'custom',
                                validator: function (v) {
                                    if (v.length === 0) return true;
                                    let valid = !/^\s*$/.test(v) && !isNaN(v);
                                    if (!valid) return false;
                                    let value = parseFloat(v);
                                    return value >= 0;
                                },
                                message: 'Please enter only positive numbers (can be decimal) or keep this field empty.'
                            }
                        ]
                    },
                    date: {
                        type: String,
                        value: ''
                    }
                })
            }
        },
        methods: {
            filter (e) {
                this.$emit('filterClicked', this.$data.form);
            },
            clearFilter (e) {
                let form = this.$data.form;

                form.cospar.value = '';
                form.norad.value = '';
                form.minp.value = '';
                form.maxp.value = '';
                form.date.value = null;

                this.$emit('clearFilterClicked');
            },
            test(e) {
                console.log(e);
              this.$data.form.date.value = null
            },
            handleResize() {
              this.isBigScreen = window.innerWidth > 600;
            }
        },
        beforeDestroy() {
          window.removeEventListener('resize', this.handleResize);
        },
        mounted() {
          window.addEventListener('resize', this.handleResize);
        }
    }
</script>

<style scoped>
    #minp {
        margin-bottom: 0;
    }

    #maxp {
        /*margin-top: 0;*/
    }

    .test {
        border-color: #ced4db!important;
    }

    #minp >>> .form-group-error {
        /*padding-bottom: 4px;*/
    }

    .form-group+.form-group {
        margin-top: 5px;
    }

    #button-form-group {
        margin-top: 5px;
    }

    #help-modal >>> .body {
        overflow-y: auto;
        max-height: 50vh;

    }

    #help-modal >>> .body {
        max-height: calc(var(--vh, 1vh) * 50);
    }

    #date-input >>> input {
      background-color: white!important;
      cursor: pointer;
    }

    ._width-65 {
          width: 65%!important;
    }

    .custom-table-left {
      text-align: left;
    }

    .custom-table-right {
      text-align: right;
    }

    .flex-container {
      display: flex;
      width: 100%;
    }

    .flex-column {
      flex-direction: column;
      width: 100%;
    }

    .element1b {
      padding-right: 5px;
      width: calc(100% - 270px);
    }

    .element1s {
    }

    .element2b {
      padding-left: 5px;
      max-width: 280px;
    }

    .element2s {
      padding-top: 5px;
    }

</style>



// WEBPACK FOOTER //
// src/components/LCDForm.vue